import {
  Box,
  Typography,
  InputAdornment,
  Link,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";

import { imageSource, Strings } from "@app/constants";
import { FormProvider, RHFTextField } from "@app/components";
import { PATH_AUTH } from "@app/routes/paths";
import { AuthLayout } from "@app/layouts";
import { useRequestState, useResponsive } from "@app/hooks";

import { login } from "../slice";
import { selectLoginState } from "../selectors";
import { loginSchema } from "../validators";

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isLargeScreen = useResponsive("down", "md");
  const methods = useForm<any>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const onSubmit = (form: any) => {
    dispatch(login(form));
  };
  const { loading } = useRequestState({
    stateSelector: selectLoginState,
    errorShown: true,
    successMessageShown: true,
  });
  return (
    <>
      <AuthLayout title={Strings.auth.logIn}>
        <Box
          sx={{
            overflowY: "auto",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Typography
            fontFamily={"Work Sans"}
            fontWeight={700}
            color={theme.palette.grey[900]}
            mb={4}
            fontSize={24}
            textAlign={isLargeScreen ? "left" : "center"}
          >
            {Strings.auth.logIn}
          </Typography>
          <FormProvider
            methods={methods}
            onSubmit={methods.handleSubmit(onSubmit, () => {
              window.scrollTo(0, 0);
            })}
          >
            <Typography
              fontFamily={"Work Sans"}
              fontWeight={500}
              color={theme.palette.grey[900]}
            >
              {Strings.auth.emailAddress}
            </Typography>
            <RHFTextField
              name="email"
              fullWidth
              placeholder="Email-Address"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img
                      alt="emailIcon"
                      loading="lazy"
                      src={imageSource.emailIcon}
                      style={{ width: 25, height: 25, objectFit: "contain" }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                mt: 1,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.palette.grey[100],
                  borderRadius: "12px",
                  "& fieldset": {
                    border: "none",
                  },
                },
              }}
            />
            <Typography
              fontFamily={"Work Sans"}
              fontWeight={500}
              color={theme.palette.grey[900]}
            >
              {Strings.auth.password}
            </Typography>
            <RHFTextField
              name="password"
              fullWidth
              type="password"
              placeholder="password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <img
                      alt="passwordIcon"
                      loading="lazy"
                      src={imageSource.passwordIcon}
                      style={{ width: 25, height: 25, objectFit: "contain" }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                mt: 1,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.palette.grey[100],
                  borderRadius: "12px",
                  "& fieldset": {
                    border: "none",
                  },
                },
              }}
            />

            <Box sx={{ textAlign: "right", mb: 3 }}>
              <Link
                underline="none"
                fontFamily={"Work Sans"}
                fontWeight={500}
                sx={{
                  fontSize: 14,
                  color: theme.palette.grey[600],
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
                onClick={() => navigate(PATH_AUTH.forgotPassword)}
              >
                {Strings.auth.forgotPassword}
              </Link>
            </Box>

            <Box display="flex" justifyContent="center" mt={4}>
              <LoadingButton
                type="submit"
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.common.white,
                  py: 1.5,
                  fontFamily: "Work Sans",
                  fontWeight: 500,
                  width: "100%",
                  maxWidth: "400px",
                }}
                loading={loading}
              >
                {Strings.auth.logIn}
              </LoadingButton>
            </Box>
          </FormProvider>
          <Typography
            fontFamily={"Work Sans"}
            fontWeight={500}
            textAlign={"center"}
            sx={{ marginTop: 5, color: theme.palette.grey[600] }}
          >
            {Strings.auth.doNotHave}
            <Link
              href="#"
              underline="none"
              fontFamily={"Work Sans"}
              fontWeight={500}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  textDecoration: "underline",
                },
                marginLeft: 0.8,
              }}
              onClick={() => navigate(PATH_AUTH.signUp)}
            >
              {Strings.auth.signUp}
            </Link>
          </Typography>
        </Box>
      </AuthLayout>
    </>
  );
};

export { LoginPage };
